/**
 * AtlasLoadingSlidesModule — the boot loading screen's background (2026-09-10):
 * the four concept paintings (`#load-slides .load-slide`, index.html)
 * crossfade IN ORDER while the screen is up. Starts on `atlas:menu-enter`
 * (the moment the loading clock starts — after the intro, if one played),
 * holds each painting LOADING_SLIDE_HOLD_MS, lazily fetches the next
 * painting one step ahead, and stops on `atlas:loading-done` (the screen is
 * removed 700 ms later by the loading module). Pure timing in
 * lib/loading_slides.ts; reduced motion switches the CSS crossfade off
 * (index.html) — the order and the cadence are the same.
 */
import type { App, IModule } from 'enginex';
import { bus } from '../bus.js';
import { LOADING_SLIDE_COUNT, LOADING_SLIDE_HOLD_MS, nextLoadingSlide } from '../lib/loading_slides.js';

export class AtlasLoadingSlidesModule implements IModule {
  readonly name = 'AtlasLoadingSlides';
  private timer = 0;
  private index = 1;
  private shown = 0;
  private startedAt = 0;

  init(_app: App): void {
    void _app;
    if (!document.getElementById('load-slides')) return; // harness / embed pages carry no screen
    bus.on('atlas:menu-enter', () => this.start());
    bus.on('atlas:loading-done', () => this.stop());
    (window as unknown as Record<string, unknown>).__loadingSlides = () => ({
      running: this.timer !== 0, index: this.index, shown: this.shown,
      elapsedMs: this.startedAt ? Math.round(performance.now() - this.startedAt) : 0,
      visible: [...document.querySelectorAll<HTMLImageElement>('#load-slides .load-slide.is-on')].map((el) => Number(el.dataset.slide)),
    });
  }

  private slide(i: number): HTMLImageElement | null {
    return document.querySelector<HTMLImageElement>(`#load-slides .load-slide[data-slide="${i}"]`);
  }

  /** Give a lazy slide its src (one step ahead of its turn, so the fade never waits on the fetch). */
  private ensureLoaded(i: number): void {
    const el = this.slide(i);
    if (el && !el.src && el.dataset.src) el.src = el.dataset.src;
  }

  private start(): void {
    if (this.timer !== 0) return;
    this.startedAt = performance.now();
    this.shown = 1;
    this.ensureLoaded(nextLoadingSlide(this.index));
    this.timer = window.setInterval(() => this.advance(), LOADING_SLIDE_HOLD_MS);
  }

  private advance(): void {
    const next = nextLoadingSlide(this.index, LOADING_SLIDE_COUNT);
    this.ensureLoaded(next);
    const cur = this.slide(this.index), nxt = this.slide(next);
    if (nxt) nxt.classList.add('is-on');   // the newcomer fades in OVER the current painting
    if (cur) cur.classList.remove('is-on'); // …which fades out beneath it (both 1.6 s)
    this.index = next;
    this.shown++;
    this.ensureLoaded(nextLoadingSlide(next, LOADING_SLIDE_COUNT));
    const label = document.getElementById('load-slide-index');
    if (label) label.textContent = String(next);
  }

  private stop(): void {
    if (this.timer !== 0) { window.clearInterval(this.timer); this.timer = 0; }
  }

  dispose(): void { this.stop(); }
}
